/**
 * Elections — committee post elections (President / Secretary / Treasurer
 * / Member). Separate from Governance/Proposal voting which handles AGM
 * yes/no resolutions. Admin curates phases; residents nominate + vote.
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Plus, Vote, ChevronRight, X, Trophy, UserCog } from 'lucide-react';
import { toast } from 'sonner';
import { get, post, getErrorMessage } from '@/lib/api';
import { PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { EmptyState } from '@/components/ui/empty-state';
import { formatDate } from '@/lib/format';

type ElectionRole = 'president' | 'secretary' | 'treasurer' | 'member' | 'other';
type ElectionStatus =
  | 'draft'
  | 'nominations_open'
  | 'nominations_closed'
  | 'voting_open'
  | 'voting_closed'
  | 'results_published'
  | 'cancelled';
type Eligibility = 'per_primary_resident' | 'per_unit_primary_owner' | 'per_resident';

const ROLE_LABEL: Record<ElectionRole, string> = {
  president: 'President',
  secretary: 'Secretary',
  treasurer: 'Treasurer',
  member: 'Member',
  other: 'Other'
};

const STATUS_LABEL: Record<ElectionStatus, string> = {
  draft: 'Draft',
  nominations_open: 'Nominations open',
  nominations_closed: 'Nominations closed',
  voting_open: 'Voting open',
  voting_closed: 'Voting closed',
  results_published: 'Results published',
  cancelled: 'Cancelled'
};

const STATUS_VARIANT: Record<ElectionStatus, 'muted' | 'info' | 'warn' | 'ok' | 'alert'> = {
  draft: 'muted',
  nominations_open: 'info',
  nominations_closed: 'warn',
  voting_open: 'info',
  voting_closed: 'warn',
  results_published: 'ok',
  cancelled: 'alert'
};

const ELIGIBILITY_LABEL: Record<Eligibility, string> = {
  per_unit_primary_owner: 'Per unit · primary owner only',
  per_primary_resident: 'Per primary resident (owner or tenant)',
  per_resident: 'Per active resident'
};

interface ElectionRow {
  id: string;
  title: string;
  description: string | null;
  role: ElectionRole;
  status: ElectionStatus;
  voter_eligibility: Eligibility;
  term_start: string | null;
  term_end: string | null;
  nomination_start: string | null;
  nomination_end: string | null;
  voting_start: string | null;
  voting_end: string | null;
  results_published_at: string | null;
  secret_ballot: boolean;
  cancelled_at: string | null;
  cancel_reason: string | null;
  created_at: string;
  _count?: { candidates: number; voter_roll: number };
}

interface ElectionDetail extends ElectionRow {
  candidates: Array<{
    id: string;
    resident_id: string;
    manifesto: string | null;
    photo_url: string | null;
    accepted_at: string | null;
    withdrawn_at: string | null;
    nominated_by_resident_id: string | null;
    resident: { id: string; full_name: string; phone: string; relation: string };
  }>;
  results: Array<{
    candidate_id: string;
    vote_count: number;
    position: number;
    declared_winner: boolean;
    candidate: { id: string; resident: { id: string; full_name: string } };
  }>;
  has_voted: boolean;
}

const CreateSchema = z.object({
  title: z.string().trim().min(2).max(160),
  description: z.string().trim().max(2000).optional().or(z.literal('')),
  role: z.enum(['president', 'secretary', 'treasurer', 'member', 'other']),
  voter_eligibility: z.enum(['per_primary_resident', 'per_unit_primary_owner', 'per_resident']),
  term_start: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'YYYY-MM-DD').optional().or(z.literal('')),
  term_end: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'YYYY-MM-DD').optional().or(z.literal('')),
  secret_ballot: z.boolean()
});
type CreateForm = z.infer<typeof CreateSchema>;

const DEFAULTS: CreateForm = {
  title: '',
  description: '',
  role: 'member',
  voter_eligibility: 'per_unit_primary_owner',
  term_start: '',
  term_end: '',
  secret_ballot: true
};

type PromotableRole = 'president' | 'secretary' | 'treasurer';

interface PromoteTarget {
  candidate_id: string;
  candidate_name: string;
  role: PromotableRole;
}

export function ElectionsPage(): JSX.Element {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [cancelTarget, setCancelTarget] = useState<ElectionRow | null>(null);
  const [cancelReason, setCancelReason] = useState('');
  const [promoteTarget, setPromoteTarget] = useState<PromoteTarget | null>(null);

  const form = useForm<CreateForm>({
    resolver: zodResolver(CreateSchema),
    defaultValues: DEFAULTS,
    mode: 'onChange'
  });

  const listQuery = useQuery({
    queryKey: ['elections'],
    queryFn: () => get<{ items: ElectionRow[]; next_cursor: string | null }>('/elections', { params: { limit: 50 } })
  });
  const items = listQuery.data?.items ?? [];

  const detailQuery = useQuery({
    queryKey: ['elections', selectedId],
    enabled: Boolean(selectedId),
    queryFn: () => get<ElectionDetail>(`/elections/${selectedId}`)
  });

  const create = useMutation({
    mutationFn: (v: CreateForm) => {
      const body: Record<string, unknown> = {
        title: v.title,
        role: v.role,
        voter_eligibility: v.voter_eligibility,
        secret_ballot: v.secret_ballot
      };
      if (v.description?.trim()) body.description = v.description.trim();
      if (v.term_start) body.term_start = v.term_start;
      if (v.term_end) body.term_end = v.term_end;
      return post<ElectionRow>('/elections', body);
    },
    onSuccess: () => {
      toast.success('Election created (draft)');
      setOpen(false);
      form.reset(DEFAULTS);
      void qc.invalidateQueries({ queryKey: ['elections'] });
    },
    onError: (e) => toast.error(getErrorMessage(e))
  });

  const advance = useMutation({
    mutationFn: (id: string) => post<ElectionRow>(`/elections/${id}/advance`, {}),
    onSuccess: (u) => {
      toast.success(`Advanced to ${STATUS_LABEL[u.status]}`);
      void qc.invalidateQueries({ queryKey: ['elections'] });
      void qc.invalidateQueries({ queryKey: ['elections', u.id] });
    },
    onError: (e) => toast.error(getErrorMessage(e))
  });

  const publish = useMutation({
    mutationFn: (id: string) => post<ElectionRow>(`/elections/${id}/publish`, {}),
    onSuccess: () => {
      toast.success('Results published');
      void qc.invalidateQueries({ queryKey: ['elections'] });
      if (selectedId) void qc.invalidateQueries({ queryKey: ['elections', selectedId] });
    },
    onError: (e) => toast.error(getErrorMessage(e))
  });

  const cancel = useMutation({
    mutationFn: (vars: { id: string; reason: string }) =>
      post<ElectionRow>(`/elections/${vars.id}/cancel`, { reason: vars.reason }),
    onSuccess: () => {
      toast.success('Election cancelled');
      setCancelTarget(null);
      setCancelReason('');
      void qc.invalidateQueries({ queryKey: ['elections'] });
    },
    onError: (e) => toast.error(getErrorMessage(e))
  });

  const promoteWinner = useMutation({
    mutationFn: (vars: { id: string; candidate_id: string; role: PromotableRole }) =>
      post<{ promoted: boolean; admin: { id: string; role: string; full_name: string; phone: string } }>(
        `/elections/${vars.id}/promote-winner`,
        { candidate_id: vars.candidate_id, role: vars.role }
      ),
    onSuccess: (data) => {
      toast.success(`${data.admin.full_name} promoted to ${data.admin.role}`);
      setPromoteTarget(null);
      if (selectedId) void qc.invalidateQueries({ queryKey: ['elections', selectedId] });
    },
    onError: (e) => toast.error(getErrorMessage(e))
  });

  const draftCount = items.filter((e) => e.status === 'draft').length;
  const liveCount = items.filter((e) =>
    ['nominations_open', 'nominations_closed', 'voting_open', 'voting_closed'].includes(e.status)
  ).length;
  const publishedCount = items.filter((e) => e.status === 'results_published').length;

  return (
    <div className="flex flex-col gap-vt-4">
      <PageHeader
        title="Elections"
        description="Committee post elections (President / Secretary / Treasurer / Member). Separate from AGM proposal voting. Admin curates phases; residents nominate + vote."
        actions={
          <Button onClick={() => setOpen(true)}>
            <Plus className="h-4 w-4" /> Create election
          </Button>
        }
      />

      <div className="grid grid-cols-2 gap-vt-3 md:grid-cols-4">
        {[
          ['Total', items.length],
          ['Draft', draftCount],
          ['Live', liveCount],
          ['Published', publishedCount]
        ].map(([label, n]) => (
          <Card key={String(label)}>
            <CardContent className="p-vt-4">
              <div className="text-vt-12 text-vt-text-muted">{label}</div>
              <div className="mt-1 text-vt-24 font-semibold text-vt-text">{n}</div>
            </CardContent>
          </Card>
        ))}
      </div>

      <Card>
        <CardContent className="p-0">
          {listQuery.isLoading ? (
            <div className="space-y-2 p-vt-4">
              {Array.from({ length: 5 }).map((_, i) => (
                <Skeleton key={i} className="h-10 w-full" />
              ))}
            </div>
          ) : items.length === 0 ? (
            <EmptyState
              icon={<Vote className="h-6 w-6" />}
              title="No elections yet"
              description="Start a draft election for a committee role."
              action={
                <Button onClick={() => setOpen(true)}>
                  <Plus className="h-4 w-4" /> Create election
                </Button>
              }
            />
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Title</TableHead>
                  <TableHead>Role</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Candidates</TableHead>
                  <TableHead>Voter roll</TableHead>
                  <TableHead>Voting window</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((e) => {
                  const canAdvance = !['results_published', 'cancelled'].includes(e.status);
                  const canPublish = e.status === 'voting_closed';
                  const canCancel = !['results_published', 'cancelled'].includes(e.status);
                  return (
                    <TableRow key={e.id}>
                      <TableCell className="font-medium text-vt-text">{e.title}</TableCell>
                      <TableCell>
                        <Badge variant="muted">{ROLE_LABEL[e.role]}</Badge>
                      </TableCell>
                      <TableCell>
                        <Badge variant={STATUS_VARIANT[e.status]}>{STATUS_LABEL[e.status]}</Badge>
                      </TableCell>
                      <TableCell className="text-vt-text-muted text-vt-13">
                        {e._count?.candidates ?? 0}
                      </TableCell>
                      <TableCell className="text-vt-text-muted text-vt-13">
                        {e._count?.voter_roll ?? 0}
                      </TableCell>
                      <TableCell className="text-vt-text-muted text-vt-12">
                        {e.voting_start && e.voting_end
                          ? `${formatDate(e.voting_start)} → ${formatDate(e.voting_end)}`
                          : '—'}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="inline-flex items-center gap-vt-1">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => setSelectedId(e.id)}
                            title="View"
                          >
                            <ChevronRight className="h-4 w-4" />
                          </Button>
                          {canAdvance ? (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => advance.mutate(e.id)}
                              disabled={advance.isPending}
                              title="Advance phase"
                            >
                              <ChevronRight className="h-4 w-4 -mr-1" />
                              <ChevronRight className="h-4 w-4" />
                            </Button>
                          ) : null}
                          {canPublish ? (
                            <Button
                              size="sm"
                              variant="primary"
                              onClick={() => publish.mutate(e.id)}
                              disabled={publish.isPending}
                              title="Publish results"
                            >
                              <Trophy className="h-4 w-4" /> Publish
                            </Button>
                          ) : null}
                          {canCancel ? (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => setCancelTarget(e)}
                              title="Cancel"
                            >
                              <X className="h-4 w-4" />
                            </Button>
                          ) : null}
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Create dialog */}
      <Dialog open={open} onOpenChange={(v) => (v ? setOpen(true) : setOpen(false))}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create election</DialogTitle>
            <DialogDescription>
              Lands as draft. Open nominations / voting + publish results from the row actions.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={form.handleSubmit((v) => create.mutate(v))} className="flex flex-col gap-vt-3">
            <div className="flex flex-col gap-vt-1">
              <Label htmlFor="el-title">Title</Label>
              <Input id="el-title" placeholder="2026–27 Committee Election" {...form.register('title')} />
              {form.formState.errors.title ? (
                <p className="text-vt-12 text-vt-alert">{form.formState.errors.title.message}</p>
              ) : null}
            </div>
            <div className="grid grid-cols-1 gap-vt-3 md:grid-cols-2">
              <div className="flex flex-col gap-vt-1">
                <Label htmlFor="el-role">Role</Label>
                <Select id="el-role" {...form.register('role')}>
                  <option value="president">President</option>
                  <option value="secretary">Secretary</option>
                  <option value="treasurer">Treasurer</option>
                  <option value="member">Member</option>
                  <option value="other">Other</option>
                </Select>
              </div>
              <div className="flex flex-col gap-vt-1">
                <Label htmlFor="el-elig">Voter eligibility</Label>
                <Select id="el-elig" {...form.register('voter_eligibility')}>
                  <option value="per_unit_primary_owner">{ELIGIBILITY_LABEL.per_unit_primary_owner}</option>
                  <option value="per_primary_resident">{ELIGIBILITY_LABEL.per_primary_resident}</option>
                  <option value="per_resident">{ELIGIBILITY_LABEL.per_resident}</option>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-1 gap-vt-3 md:grid-cols-2">
              <div className="flex flex-col gap-vt-1">
                <Label htmlFor="el-ts">Term start</Label>
                <Input id="el-ts" type="date" {...form.register('term_start')} />
              </div>
              <div className="flex flex-col gap-vt-1">
                <Label htmlFor="el-te">Term end</Label>
                <Input id="el-te" type="date" {...form.register('term_end')} />
              </div>
            </div>
            <div className="flex flex-col gap-vt-1">
              <Label htmlFor="el-desc">Description (optional)</Label>
              <Textarea id="el-desc" rows={3} placeholder="Brief context for residents" {...form.register('description')} />
            </div>
            <label className="flex items-start gap-vt-2 rounded-vt-md border border-vt-border bg-vt-surface-2 p-vt-3">
              <input
                type="checkbox"
                {...form.register('secret_ballot')}
                className="mt-0.5 h-4 w-4 accent-vt-primary"
              />
              <span className="flex-1 text-vt-13">
                <span className="font-semibold text-vt-text">Secret ballot</span>
                <span className="block text-vt-12 text-vt-text-muted">
                  Default ON. Voter→candidate link is anonymised at result publish; voter roll
                  (who voted) stays for quorum + double-vote prevention. Turn OFF for
                  transparency-focused societies where every vote is named.
                </span>
              </span>
            </label>
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={() => setOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" loading={create.isPending} loadingText="Creating…">
                Create draft
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Cancel dialog */}
      <Dialog
        open={Boolean(cancelTarget)}
        onOpenChange={(v) => (v ? null : (setCancelTarget(null), setCancelReason('')))}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Cancel election?</DialogTitle>
            <DialogDescription>
              {cancelTarget ? (
                <>
                  Cancel <span className="font-semibold">{cancelTarget.title}</span>? Status will be
                  set to <em>cancelled</em>; no further phase changes allowed.
                </>
              ) : null}
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-col gap-vt-1">
            <Label htmlFor="cancel-reason">Reason</Label>
            <Textarea
              id="cancel-reason"
              rows={2}
              maxLength={500}
              placeholder="e.g. Insufficient candidates"
              value={cancelReason}
              onChange={(e) => setCancelReason(e.target.value)}
            />
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="ghost"
              onClick={() => {
                setCancelTarget(null);
                setCancelReason('');
              }}
            >
              Keep
            </Button>
            <Button
              variant="danger"
              loading={cancel.isPending}
              loadingText="Cancelling…"
              disabled={cancelReason.trim().length < 2}
              onClick={() =>
                cancelTarget && cancel.mutate({ id: cancelTarget.id, reason: cancelReason.trim() })
              }
            >
              Cancel election
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Detail drawer */}
      <Dialog
        open={Boolean(selectedId)}
        onOpenChange={(v) => (v ? null : setSelectedId(null))}
      >
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{detailQuery.data?.title ?? 'Election'}</DialogTitle>
            <DialogDescription>
              {detailQuery.data ? (
                <>
                  {ROLE_LABEL[detailQuery.data.role]} · {STATUS_LABEL[detailQuery.data.status]} ·{' '}
                  {detailQuery.data.secret_ballot ? 'Secret ballot' : 'Named ballot'}
                </>
              ) : null}
            </DialogDescription>
          </DialogHeader>
          {detailQuery.isLoading ? (
            <div className="space-y-2">
              <Skeleton className="h-6 w-1/2" />
              <Skeleton className="h-20 w-full" />
            </div>
          ) : !detailQuery.data ? null : (
            <div className="flex flex-col gap-vt-3">
              {detailQuery.data.description ? (
                <p className="text-vt-13 text-vt-text-muted">{detailQuery.data.description}</p>
              ) : null}

              {/* Candidates */}
              <div>
                <div className="mb-vt-2 text-vt-12 font-semibold uppercase tracking-wide text-vt-text-muted">
                  Candidates ({detailQuery.data.candidates.length})
                </div>
                {detailQuery.data.candidates.length === 0 ? (
                  <p className="text-vt-12 text-vt-text-muted">No nominations yet.</p>
                ) : (
                  <ul className="flex flex-col gap-vt-2">
                    {detailQuery.data.candidates.map((c) => (
                      <li
                        key={c.id}
                        className="rounded-vt-md border border-vt-border bg-vt-surface-2 p-vt-3"
                      >
                        <div className="flex items-center justify-between gap-vt-2">
                          <div className="font-medium text-vt-text">{c.resident.full_name}</div>
                          <div className="flex items-center gap-vt-1">
                            {c.accepted_at ? (
                              <Badge variant="ok">Accepted</Badge>
                            ) : (
                              <Badge variant="warn">Pending</Badge>
                            )}
                            {c.withdrawn_at ? <Badge variant="alert">Withdrawn</Badge> : null}
                          </div>
                        </div>
                        {c.manifesto ? (
                          <p className="mt-vt-1 text-vt-12 text-vt-text-muted">{c.manifesto}</p>
                        ) : null}
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              {/* Results */}
              {detailQuery.data.results.length > 0 ? (
                <div>
                  <div className="mb-vt-2 text-vt-12 font-semibold uppercase tracking-wide text-vt-text-muted">
                    Results
                  </div>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Position</TableHead>
                        <TableHead>Candidate</TableHead>
                        <TableHead className="text-right">Votes</TableHead>
                        <TableHead className="text-right">Action</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {detailQuery.data.results.map((r) => {
                        const electionRole = detailQuery.data!.role;
                        const isPromotable =
                          r.declared_winner &&
                          (electionRole === 'president' ||
                            electionRole === 'secretary' ||
                            electionRole === 'treasurer');
                        return (
                          <TableRow key={r.candidate_id}>
                            <TableCell className="font-mono text-vt-12">
                              #{r.position}
                              {r.declared_winner ? (
                                <Badge variant="ok" className="ml-2">
                                  <Trophy className="h-3 w-3" /> Winner
                                </Badge>
                              ) : null}
                            </TableCell>
                            <TableCell className="font-medium text-vt-text">
                              {r.candidate.resident.full_name}
                            </TableCell>
                            <TableCell className="text-right font-mono">{r.vote_count}</TableCell>
                            <TableCell className="text-right">
                              {isPromotable ? (
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() =>
                                    setPromoteTarget({
                                      candidate_id: r.candidate_id,
                                      candidate_name: r.candidate.resident.full_name,
                                      role: electionRole as PromotableRole
                                    })
                                  }
                                  title={`Promote to ${ROLE_LABEL[electionRole]}`}
                                >
                                  <UserCog className="h-4 w-4" /> Promote
                                </Button>
                              ) : null}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              ) : null}

              {detailQuery.data.cancel_reason ? (
                <p className="text-vt-12 text-vt-alert">
                  Cancelled: {detailQuery.data.cancel_reason}
                </p>
              ) : null}
            </div>
          )}
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => setSelectedId(null)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Promote-winner confirmation */}
      <ConfirmDialog
        open={Boolean(promoteTarget)}
        onOpenChange={(v) => (v ? null : setPromoteTarget(null))}
        title="Promote winner?"
        description={
          promoteTarget ? (
            <>
              This will set <span className="font-semibold">{promoteTarget.candidate_name}</span>{' '}
              as the society&apos;s <span className="font-semibold">{ROLE_LABEL[promoteTarget.role]}</span>.
              They will be able to sign in with their existing phone number and gain{' '}
              {ROLE_LABEL[promoteTarget.role]} permissions across the system. If they already
              have an admin role, it will be replaced.
            </>
          ) : (
            'Promote the declared winner to the matching committee role?'
          )
        }
        confirmLabel="Promote"
        variant="default"
        loading={promoteWinner.isPending}
        onConfirm={() => {
          if (!promoteTarget || !selectedId) return;
          promoteWinner.mutate({
            id: selectedId,
            candidate_id: promoteTarget.candidate_id,
            role: promoteTarget.role
          });
        }}
      />
    </div>
  );
}
